<!--
  SCHEDA CLIENTE — sul modello della creazione cliente e della scheda del
  gestionale admin:
  · i dati si LEGGONO, e si modificano solo entrando in modifica;
  · se cambia l'indirizzo lo si verifica, e al salvataggio si mandano anche le
    coordinate (il backend in modifica non le ricalcola, vedi
    `schedaClienteAPI.js`);
  · i pacchetti valet stanno DENTRO l'auto a cui appartengono (valet all'anno,
    durata, prezzo a rate), come in creazione cliente;
  · le prenotazioni del cliente si vedono e si aprono.

  Tutti i ruoli del partner vedono e fanno le stesse cose.

  ⚠️ Si mostrano solo i pacchetti del PROPRIO partner: sulla stessa auto possono
  esserci pacchetti di altri partner, con i loro prezzi.
-->
<template>
  <div
    v-loading="stato === 'carico'"
    class="csd"
  >
    <header class="csd__head">
      <button
        type="button"
        class="csd__back"
        aria-label="Torna a Clienti"
        @click="indietro"
      >
        ‹
      </button>
      <span
        class="csd__avatar"
        aria-hidden="true"
      >{{ iniziali }}</span>
      <div class="csd__who">
        <h1 class="csd__title">
          {{ nomeCliente }}
          <StatusPill
            v-if="cliente.is_alive === false"
            kind="muted"
            label="Non attivo"
          />
        </h1>
        <p class="csd__sub">
          {{ contatti }}
        </p>
      </div>
      <span class="csd__spacer" />
      <router-link
        v-if="puoPrenotare && stato === 'ok'"
        :to="rottaPrenotazione()"
        class="csd__cta"
      >
        Nuova prenotazione
      </router-link>
    </header>

    <div
      v-if="stato === 'errore'"
      class="csd__banner csd__banner--danger"
      role="alert"
    >
      <span
        class="csd__bannerIcon"
        aria-hidden="true"
      >!</span>
      <div class="csd__bannerBody">
        Non riesco a leggere la scheda del cliente.
        <div class="csd__bannerActions">
          <el-button
            size="small"
            @click="carica"
          >
            Riprova
          </el-button>
        </div>
      </div>
    </div>
    <div
      v-else-if="stato === 'assente'"
      class="csd__banner csd__banner--attn"
      role="alert"
    >
      <span
        class="csd__bannerIcon"
        aria-hidden="true"
      >!</span>
      <div class="csd__bannerBody">
        Questo cliente non è fra i tuoi clienti, oppure non esiste più.
      </div>
    </div>

    <div
      v-if="stato === 'ok'"
      class="csd__grid"
    >
      <div class="fx csd__col">
        <!-- ================= DATI ================= -->
        <section class="form-card">
          <div class="form-card__head">
            <h3>{{ isAzienda ? 'Dati azienda e referente' : 'Dati anagrafici' }}</h3>
            <span class="form-card__spacer" />
            <el-button
              v-if="!modifica"
              size="small"
              @click="iniziaModifica"
            >
              Modifica
            </el-button>
          </div>

          <div class="csd__body">
            <dl
              v-if="!modifica"
              class="csd__kvGrid"
            >
              <div
                v-for="c in campiLettura"
                :key="c.label"
                class="csd__kvItem"
                :class="{ 'csd__kvItem--full': c.full }"
              >
                <dt>{{ c.label }}</dt>
                <dd :class="{ 'is-empty': !c.value }">
                  {{ c.value || '—' }}
                </dd>
              </div>
            </dl>

            <template v-else>
              <div class="csd__grid3">
                <template v-if="isAzienda">
                  <div class="csd__f csd__f--full">
                    <label
                      class="campo-label"
                      for="csd-ragione"
                    >Ragione sociale <span class="csd__req">*</span></label>
                    <el-input
                      id="csd-ragione"
                      v-model="bozza.company_name"
                    />
                  </div>
                  <div class="csd__f">
                    <label
                      class="campo-label"
                      for="csd-piva"
                    >Partita IVA</label>
                    <el-input
                      id="csd-piva"
                      v-model="bozza.piva"
                    />
                  </div>
                  <div class="csd__f">
                    <label
                      class="campo-label"
                      for="csd-sdi"
                    >Codice SDI</label>
                    <el-input
                      id="csd-sdi"
                      v-model="bozza.SDI"
                    />
                  </div>
                  <div class="csd__f">
                    <label
                      class="campo-label"
                      for="csd-pec"
                    >PEC</label>
                    <el-input
                      id="csd-pec"
                      v-model="bozza.PEC"
                    />
                  </div>
                </template>

                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-nome"
                  >{{ isAzienda ? 'Nome referente' : 'Nome' }} <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-nome"
                    v-model="bozza.name"
                  />
                </div>
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-cognome"
                  >{{ isAzienda ? 'Cognome referente' : 'Cognome' }} <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-cognome"
                    v-model="bozza.surname"
                  />
                </div>
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-telefono"
                  >Telefono <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-telefono"
                    v-model="bozza.telephone_number"
                    autocomplete="off"
                  />
                </div>
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-cf"
                  >Codice fiscale</label>
                  <el-input
                    id="csd-cf"
                    v-model="bozza.CF"
                  />
                </div>
                <div
                  v-if="!isAzienda"
                  class="csd__f"
                >
                  <label
                    class="campo-label"
                    for="csd-nascita"
                  >Data di nascita</label>
                  <el-date-picker
                    id="csd-nascita"
                    v-model="bozza.date_birth"
                    type="date"
                    format="DD/MM/YYYY"
                    value-format="YYYY-MM-DD"
                    placeholder="Facoltativa"
                  />
                </div>
                <div class="csd__f csd__f--2">
                  <label
                    class="campo-label"
                    for="csd-mail"
                  >Email</label>
                  <el-input
                    id="csd-mail"
                    :model-value="cliente.mail"
                    disabled
                  />
                  <span class="csd__help">È l'accesso del cliente all'app: da qui non si cambia.</span>
                </div>
              </div>

              <h4 class="csd__h4">
                Indirizzo
              </h4>
              <div class="csd__grid3">
                <div class="csd__f csd__f--2">
                  <label
                    class="campo-label"
                    for="csd-via"
                  >Via / Piazza <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-via"
                    v-model="bozza.address"
                    @blur="verificaIndirizzo()"
                  />
                </div>
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-civico"
                  >Civico <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-civico"
                    v-model="bozza.street_number"
                    @blur="verificaIndirizzo()"
                  />
                </div>
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-cap"
                  >CAP <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-cap"
                    v-model="bozza.cap"
                    maxlength="5"
                    @blur="verificaIndirizzo()"
                  />
                </div>
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-citta"
                  >Città <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-citta"
                    v-model="bozza.city"
                    @blur="verificaIndirizzo()"
                  />
                </div>
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-provincia"
                  >Provincia <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-provincia"
                    v-model="bozza.province"
                    maxlength="2"
                    placeholder="es. BG"
                    @blur="onProvinciaBlur"
                  />
                </div>
              </div>

              <template v-if="indirizzoCambiato">
                <span
                  v-if="geo.stato === 'verifica'"
                  class="csd__help"
                >Verifica dell'indirizzo in corso…</span>
                <div
                  v-else-if="geo.stato === 'ok'"
                  class="csd__banner csd__banner--ok"
                >
                  <span
                    class="csd__bannerIcon"
                    aria-hidden="true"
                  >✓</span>
                  <div class="csd__bannerBody">
                    Indirizzo trovato: <strong>{{ geo.label }}</strong>
                  </div>
                </div>
                <div
                  v-else-if="geo.stato === 'ko'"
                  class="csd__banner csd__banner--danger"
                  role="alert"
                >
                  <span
                    class="csd__bannerIcon"
                    aria-hidden="true"
                  >!</span>
                  <div class="csd__bannerBody">
                    <strong>Indirizzo non trovato.</strong> Controlla via, civico e CAP: finché non si
                    trova, l'indirizzo nuovo non si può salvare.
                    <div class="csd__bannerActions">
                      <el-button
                        size="small"
                        @click="verificaIndirizzo(true)"
                      >
                        Riprova la verifica
                      </el-button>
                    </div>
                  </div>
                </div>
                <span
                  v-else-if="geo.stato === 'nd'"
                  class="csd__help"
                >Verifica non disponibile in questo momento: riprova tra poco.</span>
              </template>

              <div class="csd__formActions">
                <span
                  v-if="tentato && primoProblema"
                  class="csd__err"
                  role="alert"
                >{{ primoProblema }}</span>
                <span
                  v-else
                  class="form-actions__hint"
                >I campi con * sono obbligatori</span>
                <span class="csd__spacer" />
                <el-button @click="annullaModifica">
                  Annulla
                </el-button>
                <el-button
                  type="primary"
                  :loading="salvando"
                  @click="salvaCliente"
                >
                  Salva modifiche
                </el-button>
              </div>
            </template>
          </div>
        </section>

        <!-- ================= AUTO E VALET ================= -->
        <section class="form-card">
          <div class="form-card__head">
            <h3>Auto e valet pickup</h3>
            <span class="form-card__spacer" />
            <span class="form-card__hint">Il pacchetto valet appartiene all'auto, non al cliente.</span>
          </div>

          <div class="csd__body">
            <p
              v-if="!auto.length && !nuovaAuto"
              class="csd__empty"
            >
              Nessuna auto: aggiungine una per poter prenotare.
            </p>

            <article
              v-for="a in auto"
              :key="a.id"
              class="csd__car"
            >
              <div class="csd__carHead">
                <span class="csd__plate">{{ a.targa || '—' }}</span>
                <span class="csd__carName">{{ nomeAuto(a) }}</span>
                <span class="csd__carTag">{{ cambio(a.transmission) }}</span>
                <span class="csd__spacer" />
                <router-link
                  v-if="puoPrenotare && modificaAuto.id !== a.id"
                  :to="rottaPrenotazione(a.id)"
                  class="csd__carLink"
                >
                  Prenota con quest'auto
                </router-link>
                <el-button
                  v-if="modificaAuto.id !== a.id"
                  text
                  size="small"
                  @click="apriModificaAuto(a)"
                >
                  Modifica
                </el-button>
              </div>
              <div
                v-if="a.intestatario && modificaAuto.id !== a.id"
                class="csd__meta"
              >
                Intestata a {{ a.intestatario }}
              </div>

              <!-- modifica dell'auto -->
              <template v-if="modificaAuto.id === a.id">
                <div class="csd__grid3">
                  <div class="csd__f">
                    <label
                      class="campo-label"
                      :for="`csd-targa-${a.id}`"
                    >Targa/Telaio <span class="csd__req">*</span></label>
                    <el-input
                      :id="`csd-targa-${a.id}`"
                      v-model="modificaAuto.targa"
                      class="csd__plateInput"
                      maxlength="17"
                    />
                  </div>
                  <div class="csd__f">
                    <label
                      class="campo-label"
                      :for="`csd-marca-${a.id}`"
                    >Marca <span class="csd__req">*</span></label>
                    <el-input
                      :id="`csd-marca-${a.id}`"
                      v-model="modificaAuto.marca"
                    />
                  </div>
                  <div class="csd__f">
                    <label
                      class="campo-label"
                      :for="`csd-modello-${a.id}`"
                    >Modello <span class="csd__req">*</span></label>
                    <el-input
                      :id="`csd-modello-${a.id}`"
                      v-model="modificaAuto.modello"
                    />
                  </div>
                  <div class="csd__f csd__f--2">
                    <label
                      class="campo-label"
                      :for="`csd-intestatario-${a.id}`"
                    >Intestatario</label>
                    <el-input
                      :id="`csd-intestatario-${a.id}`"
                      v-model="modificaAuto.intestatario"
                    />
                  </div>
                  <div class="csd__f">
                    <span class="campo-label">Cambio</span>
                    <div
                      class="csd__seg"
                      role="radiogroup"
                      aria-label="Cambio"
                    >
                      <button
                        v-for="o in cambiModifica"
                        :key="o.value"
                        type="button"
                        role="radio"
                        :aria-checked="modificaAuto.transmission === o.value"
                        :class="{ 'is-on': modificaAuto.transmission === o.value }"
                        @click="modificaAuto.transmission = o.value"
                      >
                        {{ o.label }}
                      </button>
                    </div>
                  </div>
                </div>
                <div class="csd__formActions">
                  <span class="csd__spacer" />
                  <el-button
                    size="small"
                    @click="modificaAuto.id = ''"
                  >
                    Annulla
                  </el-button>
                  <el-button
                    size="small"
                    type="primary"
                    :loading="modificaAuto.salvando"
                    @click="salvaModificaAuto"
                  >
                    Salva auto
                  </el-button>
                </div>
              </template>

              <!-- pacchetti del partner su quest'auto -->
              <div class="csd__valets">
                <div
                  v-for="m in pacchettiDi(a)"
                  :key="m.id"
                  class="csd__valet"
                >
                  <div class="csd__valetTop">
                    <span class="csd__valetTitle">{{ titoloPacchetto(m) }}</span>
                    <StatusPill
                      :kind="statoPacchetto(m).kind"
                      :label="statoPacchetto(m).label"
                    />
                  </div>
                  <template v-if="m.accepted === true">
                    <div class="csd__bar">
                      <span
                        :class="{ 'is-low': percentuale(m) <= 20 }"
                        :style="{ width: `${percentuale(m)}%` }"
                      />
                    </div>
                    <div class="csd__valetMeta">
                      <span><b>{{ num(m.residual_valet) }}</b> di {{ num(m.valet) }} residui</span>
                      <span>Scade il {{ data(m.expiration_valet) }}</span>
                      <!-- Quando è stato speso: le date delle prenotazioni che
                           hanno consumato un valet di questo pacchetto. -->
                      <span v-if="usiDi(m).length">
                        Usato il {{ usiDi(m).map((d) => data(d)).join(', ') }}
                      </span>
                      <!-- Il prezzo AL CLIENTE, con le tariffe del partner:
                           `imponibile` è il prezzo Simba e non si mostra. -->
                      <span v-if="prezzoCliente(m) !== null">{{ euro(prezzoCliente(m)) }} + IVA al cliente</span>
                    </div>
                  </template>
                  <div
                    v-else
                    class="csd__valetMeta"
                  >
                    <span>{{ num(m.valet) }} valet</span>
                    <span v-if="prezzoCliente(m) !== null">{{ euro(prezzoCliente(m)) }} + IVA al cliente</span>
                    <span v-if="m.data_reg">Proposto il {{ data(m.data_reg) }}</span>
                  </div>
                </div>
                <p
                  v-if="!pacchettiDi(a).length && proposta.carId !== a.id"
                  class="csd__noValet"
                >
                  Nessun pacchetto valet su quest'auto.
                </p>
              </div>

              <!-- proposta di un pacchetto -->
              <div
                v-if="proposta.carId === a.id"
                class="csd__proposal"
              >
                <div class="csd__proposalTitle">
                  Nuovo pacchetto valet per {{ a.targa || nomeAuto(a) }}
                </div>
                <!-- Prezzo dal listino: distanza casa del cliente → sede,
                     pickup all'anno e mesi. Niente più importi a mano. -->
                <PropostaValet
                  :indirizzo="cliente"
                  :sedi="contesto.sedi"
                  @change="(e) => (proposta.esito = e)"
                />

                <p class="csd__note">
                  La proposta arriva a noi di Simba: saremo noi ad accettarla. Fino ad allora
                  il pacchetto resta «In attesa di Simba» e non si può usare nelle prenotazioni.
                </p>

                <div class="csd__formActions">
                  <span
                    v-if="proposta.tentato && problemaProposta"
                    class="csd__err"
                    role="alert"
                  >{{ problemaProposta }}</span>
                  <span class="csd__spacer" />
                  <el-button
                    size="small"
                    @click="proposta.carId = ''"
                  >
                    Annulla
                  </el-button>
                  <el-button
                    size="small"
                    type="primary"
                    :loading="proposta.invio"
                    @click="inviaPropostaValet(a)"
                  >
                    Proponi a Simba
                  </el-button>
                </div>
              </div>
              <el-button
                v-else-if="modificaAuto.id !== a.id"
                size="small"
                class="csd__propose"
                @click="apriProposta(a)"
              >
                + Proponi un pacchetto valet
              </el-button>
            </article>

            <!-- nuova auto -->
            <article
              v-if="nuovaAuto"
              class="csd__car csd__car--new"
            >
              <div class="csd__carHead">
                <span class="csd__proposalTitle">Nuova auto</span>
              </div>
              <div class="csd__grid3">
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-nuova-targa"
                  >Targa/Telaio <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-nuova-targa"
                    v-model="nuovaAuto.targa"
                    class="csd__plateInput"
                    maxlength="17"
                    placeholder="es. FA123ZZ o n° di telaio"
                  />
                </div>
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-nuova-marca"
                  >Marca <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-nuova-marca"
                    v-model="nuovaAuto.marca"
                    placeholder="es. BMW"
                  />
                </div>
                <div class="csd__f">
                  <label
                    class="campo-label"
                    for="csd-nuova-modello"
                  >Modello <span class="csd__req">*</span></label>
                  <el-input
                    id="csd-nuova-modello"
                    v-model="nuovaAuto.modello"
                    placeholder="es. Serie 3"
                  />
                </div>
                <div class="csd__f csd__f--2">
                  <label
                    class="campo-label"
                    for="csd-nuova-intestatario"
                  >Intestatario</label>
                  <el-input
                    id="csd-nuova-intestatario"
                    v-model="nuovaAuto.intestatario"
                  />
                </div>
                <div class="csd__f">
                  <span class="campo-label">Cambio</span>
                  <div
                    class="csd__seg"
                    role="radiogroup"
                    aria-label="Cambio della nuova auto"
                  >
                    <button
                      v-for="o in CAMBI"
                      :key="o.value"
                      type="button"
                      role="radio"
                      :aria-checked="nuovaAuto.transmission === o.value"
                      :class="{ 'is-on': nuovaAuto.transmission === o.value }"
                      @click="nuovaAuto.transmission = o.value"
                    >
                      {{ o.label }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="csd__formActions">
                <span class="csd__help">Il pacchetto valet si propone dopo, sull'auto salvata.</span>
                <span class="csd__spacer" />
                <el-button
                  size="small"
                  @click="nuovaAuto = null"
                >
                  Annulla
                </el-button>
                <el-button
                  size="small"
                  type="primary"
                  :loading="nuovaAuto.salvando"
                  @click="salvaNuovaAuto"
                >
                  Aggiungi auto
                </el-button>
              </div>
            </article>
            <el-button
              v-else
              class="csd__addCar"
              @click="apriNuovaAuto"
            >
              + Aggiungi un'auto
            </el-button>
          </div>
        </section>

        <!-- ================= PRENOTAZIONI ================= -->
        <section class="form-card">
          <div class="form-card__head">
            <h3>Prenotazioni</h3>
            <span class="csd__count">{{ prenotazioni.righe.length }}{{ prenotazioni.troncato ? '+' : '' }}</span>
          </div>
          <div class="csd__list">
            <p
              v-if="prenotazioni.errore"
              class="csd__empty csd__empty--pad"
            >
              Non riesco a leggere le prenotazioni del cliente.
            </p>
            <p
              v-else-if="!prenotazioni.righe.length"
              class="csd__empty csd__empty--pad"
            >
              Nessuna prenotazione per questo cliente.
            </p>
            <button
              v-for="b in prenotazioni.righe"
              :key="b.id"
              type="button"
              class="csd__bk"
              @click="apriPrenotazione(b)"
            >
              <span class="csd__bkDate">{{ dataOra(b.data) }}</span>
              <span><ServiceChip :service="servizioDi(b)" /></span>
              <span class="csd__bkCar">{{ (b.car && b.car.targa) || '—' }}</span>
              <span class="csd__bkSede">{{ sede(b) }}</span>
              <StatusPill
                :kind="bookingState(b.state).kind"
                :label="bookingState(b.state).label"
              />
              <span
                class="csd__chev"
                aria-hidden="true"
              >›</span>
            </button>
            <p
              v-if="prenotazioni.troncato"
              class="csd__hint"
            >
              Sono mostrate le {{ LIMITE_PRENOTAZIONI }} più recenti.
            </p>
          </div>
        </section>
      </div>

      <!-- ================= RIEPILOGO ================= -->
      <aside class="csd__aside">
        <section class="form-card">
          <div class="form-card__head">
            <h3>Riepilogo</h3>
          </div>
          <div class="csd__body csd__body--kv">
            <div class="csd__kv">
              <span>Prenotazioni</span>
              <b>{{ prenotazioni.righe.length }}{{ prenotazioni.troncato ? '+' : '' }}</b>
            </div>
            <div class="csd__kv">
              <span>Ultima prenotazione</span>
              <b>{{ ultimaPrenotazione }}</b>
            </div>
            <div class="csd__kv">
              <span>Auto</span>
              <b>{{ auto.length }}</b>
            </div>
            <div class="csd__kv">
              <span>Valet utilizzabili</span>
              <b :class="{ 'csd__accent': valetUtilizzabili > 0 }">{{ valetUtilizzabili }}</b>
            </div>
            <div class="csd__kv">
              <span>Pacchetti in attesa del cliente</span>
              <b :class="{ 'csd__attn': inAttesa > 0 }">{{ inAttesa }}</b>
            </div>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';
import { ElMessage, ElMessageBox } from 'element-plus';
import { isLoggedStore } from '../store/logged';
import { messageForError } from '../services/httpErrors';
import { abilitato } from '../services/servizi';
import { bookingState } from '../services/agendaRows';
import { consumiValet, listinoValetPartner, prezzoClientePacchetto, statoPacchetto } from '../services/clientiAPI';
import {
  contestoPartner,
  geolocalizza,
  proponiPacchetto,
} from '../services/nuovoClienteAPI';
import {
  LIMITE_PRENOTAZIONI,
  aggiornaAuto,
  aggiornaCliente,
  creaAuto,
  leggiAuto,
  leggiCliente,
  leggiPrenotazioni,
} from '../services/schedaClienteAPI';
import ServiceChip from '../Components/ui/ServiceChip.vue';
import StatusPill from '../Components/ui/StatusPill.vue';
import PropostaValet from '../Components/PropostaValet.vue';

const CAMBI = [
  { value: 'automatic', label: 'Automatico' },
  { value: 'manual', label: 'Manuale' },
];
// ⚠️ A database esiste anche `both` (il driver deve saper guidare entrambi):
// nel sandbox lo hanno tutte le auto di prova. In creazione non si propone,
// ma un'auto che ce l'ha deve poterlo tenere quando la si modifica.
const CAMBIO_ENTRAMBI = { value: 'both', label: 'Entrambi' };

const CAMPI_INDIRIZZO = ['address', 'street_number', 'cap', 'city', 'province'];

const euroFmt = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

const pulito = (v) => String(v ?? '').trim();
const chiaveIndirizzo = (o) => CAMPI_INDIRIZZO.map((k) => pulito(o[k]).toLowerCase()).join('|');

export default {
  name: 'CustomerAliveDetailPage',
  components: { ServiceChip, StatusPill, PropostaValet },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = isLoggedStore();
    const id = computed(() => String(route.params.id || ''));

    // carico | ok | errore | assente
    const stato = ref('carico');
    const cliente = ref({});
    const auto = ref([]);
    const prenotazioni = reactive({ righe: [], troncato: false, errore: false });
    const contesto = reactive({ partnerId: store.partner?.id || null, sedi: [] });

    // Listino valet del partner: serve a mostrare il prezzo AL CLIENTE dei
    // pacchetti, che sul pacchetto non è salvato. Se non arriva, il prezzo
    // semplicemente non si mostra: mai quello Simba al suo posto.
    const fasceValet = ref([]);
    const caricaListinoValet = async () => {
      if (!contesto.partnerId) return;
      try {
        fasceValet.value = await listinoValetPartner(contesto.partnerId);
      } catch (e) {
        console.warn('Listino valet non letto:', e?.response?.status);
        fasceValet.value = [];
      }
    };
    const prezzoCliente = (m) => prezzoClientePacchetto(m, fasceValet.value);

    // Quando ogni pacchetto è stato speso: { [minuteId]: [date] }. Si chiede
    // dopo le auto, perché servono gli id dei pacchetti; se non arriva, la
    // scheda resta leggibile senza quella riga.
    const usiValet = ref({});
    const usiDi = (m) => usiValet.value[m?.id] || [];

    const caricaAuto = async () => {
      auto.value = await leggiAuto(id.value);
      const ids = auto.value.flatMap((a) => (a.minutes || []).map((m) => m.id));
      try {
        usiValet.value = await consumiValet(ids);
      } catch (e) {
        console.warn('Utilizzi del valet non letti:', e?.response?.status);
      }
    };

    // Le prenotazioni non bloccano la scheda: se non arrivano lo si dice nel loro riquadro.
    const caricaPrenotazioni = async () => {
      try {
        const r = await leggiPrenotazioni(id.value);
        prenotazioni.righe = r.rows;
        prenotazioni.troncato = r.troncato;
        prenotazioni.errore = false;
      } catch (e) {
        console.error('Prenotazioni del cliente non lette:', e);
        prenotazioni.righe = [];
        prenotazioni.troncato = false;
        prenotazioni.errore = true;
      }
    };

    const carica = async () => {
      stato.value = 'carico';
      try {
        const [c, ctx] = await Promise.all([
          leggiCliente(id.value),
          // Le sedi servono solo alle auto nuove: senza, la scheda resta leggibile.
          contestoPartner(store.employee?.id || store.id).catch(() => null),
        ]);
        cliente.value = c || {};
        if (ctx) {
          contesto.sedi = ctx.sedi;
          contesto.partnerId = contesto.partnerId || ctx.partnerId;
        }
        await Promise.all([caricaAuto(), caricaPrenotazioni(), caricaListinoValet()]);
        stato.value = 'ok';
      } catch (e) {
        const s = e?.response?.status;
        console.error('Scheda cliente non letta:', e);
        stato.value = s === 404 || s === 403 ? 'assente' : 'errore';
      }
    };
    onMounted(carica);
    watch(id, (nuovo, vecchio) => {
      if (nuovo && nuovo !== vecchio) carica();
    });

    const indietro = () => (window.history.state?.back ? router.back() : router.push('/customer'));

    // ---- intestazione -------------------------------------------------
    const isAzienda = computed(() => Boolean(pulito(cliente.value.company_name)));
    const nomePersona = computed(() =>
      [cliente.value.name, cliente.value.surname].map(pulito).filter(Boolean).join(' '),
    );
    const nomeCliente = computed(
      () => pulito(cliente.value.company_name) || nomePersona.value || 'Cliente',
    );
    const iniziali = computed(
      () => nomeCliente.value.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?',
    );
    const contatti = computed(() =>
      [cliente.value.mail, cliente.value.telephone_number].map(pulito).filter(Boolean).join(' · '),
    );
    const puoPrenotare = computed(() => store.bookingType === true);

    // «Nuova prenotazione» dalla scheda: se il partner fa Pickup &
    // Delivery si entra direttamente lì, con il cliente (ed eventualmente
    // l'auto) già scelti.
    const rottaPrenotazione = (autoId = '') => {
      if (!abilitato(store, 'pickup')) return '/prenotazione/nuova';
      const q = new URLSearchParams({ servizio: 'pickup', cliente: id.value });
      if (autoId) q.set('auto', autoId);
      return `/prenotazione/nuova?${q.toString()}`;
    };

    // ---- formattazione ------------------------------------------------
    const indirizzo = (o) => {
      const via = [o.address, o.street_number].map(pulito).filter(Boolean).join(' ');
      const provincia = pulito(o.province);
      const citta = [pulito(o.cap), pulito(o.city), provincia ? `(${provincia})` : '']
        .filter(Boolean)
        .join(' ');
      return [via, citta].filter(Boolean).join(', ');
    };
    const data = (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '—');
    const dataOra = (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—');
    const euro = (v) => (v === null || v === undefined || v === '' ? '—' : euroFmt.format(Number(v)));
    const num = (v) => Number(v) || 0;

    const campiLettura = computed(() => {
      const c = cliente.value;
      const out = [];
      if (isAzienda.value) {
        out.push(
          { label: 'Ragione sociale', value: c.company_name, full: true },
          { label: 'Partita IVA', value: c.piva },
          { label: 'Codice SDI', value: c.SDI },
          { label: 'PEC', value: c.PEC },
        );
      }
      out.push(
        { label: isAzienda.value ? 'Referente' : 'Nome e cognome', value: nomePersona.value },
        { label: 'Telefono', value: c.telephone_number },
        { label: 'Email', value: c.mail },
        { label: 'Codice fiscale', value: c.CF },
      );
      if (!isAzienda.value) {
        out.push({ label: 'Data di nascita', value: c.date_birth ? data(c.date_birth) : '' });
      }
      out.push({ label: 'Indirizzo', value: indirizzo(c), full: true });
      return out;
    });

    // ---- modifica dei dati --------------------------------------------
    const modifica = ref(false);
    const tentato = ref(false);
    const salvando = ref(false);
    const bozza = reactive({});

    // idle | verifica | ok | ko | nd (non disponibile)
    const geo = reactive({ stato: 'idle', label: '', chiave: '', punto: null });

    const iniziaModifica = () => {
      [
        'company_name', 'piva', 'SDI', 'PEC', 'name', 'surname', 'telephone_number', 'CF',
        ...CAMPI_INDIRIZZO,
      ].forEach((k) => {
        bozza[k] = cliente.value[k] ?? '';
      });
      // Dal server arriva un datetime: si porta al giorno in ora locale, non tagliando la stringa.
      bozza.date_birth = cliente.value.date_birth ? dayjs(cliente.value.date_birth).format('YYYY-MM-DD') : null;
      Object.assign(geo, { stato: 'idle', label: '', chiave: '', punto: null });
      tentato.value = false;
      modifica.value = true;
    };
    const annullaModifica = () => {
      modifica.value = false;
    };

    const indirizzoCambiato = computed(
      () => modifica.value && chiaveIndirizzo(bozza) !== chiaveIndirizzo(cliente.value),
    );
    const indirizzoCompleto = computed(() => CAMPI_INDIRIZZO.every((k) => pulito(bozza[k])));

    // L'esito vale per l'indirizzo verificato, non per quello di adesso.
    watch(
      () => chiaveIndirizzo(bozza),
      (k) => {
        if (k !== geo.chiave) Object.assign(geo, { stato: 'idle', label: '', punto: null });
      },
    );

    const verificaIndirizzo = async (forza = false) => {
      if (!indirizzoCambiato.value || !indirizzoCompleto.value) return;
      const k = chiaveIndirizzo(bozza);
      if (!forza && k === geo.chiave && geo.stato !== 'idle') return;
      geo.chiave = k;
      geo.stato = 'verifica';
      try {
        const r = await geolocalizza({
          address: pulito(bozza.address),
          street_number: pulito(bozza.street_number),
          cap: pulito(bozza.cap),
          city: pulito(bozza.city),
          province: pulito(bozza.province).toUpperCase(),
        });
        if (k !== chiaveIndirizzo(bozza)) return;
        geo.label = r?.label || '';
        geo.punto = r?.point || null;
        geo.stato = r?.point ? 'ok' : 'ko';
      } catch (e) {
        if (k !== chiaveIndirizzo(bozza)) return;
        // 500 = il geocoder non l'ha trovato; 503, rete e il resto non dicono niente sull'indirizzo.
        geo.stato = e?.response?.status === 500 ? 'ko' : 'nd';
      }
    };

    const onProvinciaBlur = () => {
      bozza.province = pulito(bozza.province).toUpperCase();
      verificaIndirizzo();
    };

    const primoProblema = computed(() => {
      if (isAzienda.value && !pulito(bozza.company_name)) return 'Manca la ragione sociale';
      if (!pulito(bozza.name)) return isAzienda.value ? 'Manca il nome del referente' : 'Manca il nome';
      if (!pulito(bozza.surname)) return isAzienda.value ? 'Manca il cognome del referente' : 'Manca il cognome';
      if (!pulito(bozza.telephone_number)) return 'Manca il telefono';
      if (!indirizzoCompleto.value) return 'Indirizzo incompleto: servono via, civico, CAP, città e provincia';
      if (indirizzoCambiato.value && geo.stato === 'ko') return 'Indirizzo non trovato: controlla via, civico e CAP';
      if (indirizzoCambiato.value && geo.stato !== 'ok') return "Non riesco a verificare l'indirizzo: riprova tra poco";
      return '';
    });

    const salvaCliente = async () => {
      tentato.value = true;
      if (indirizzoCambiato.value && geo.stato !== 'ok' && indirizzoCompleto.value) {
        await verificaIndirizzo(true);
      }
      if (primoProblema.value) {
        ElMessage({ type: 'warning', message: primoProblema.value });
        return;
      }

      const campi = {
        name: pulito(bozza.name),
        surname: pulito(bozza.surname),
        telephone_number: pulito(bozza.telephone_number),
        CF: pulito(bozza.CF) || null,
      };
      if (isAzienda.value) {
        Object.assign(campi, {
          company_name: pulito(bozza.company_name),
          piva: pulito(bozza.piva) || null,
          SDI: pulito(bozza.SDI) || null,
          PEC: pulito(bozza.PEC) || null,
        });
      } else {
        campi.date_birth = bozza.date_birth || null;
      }
      // ⚠️ Indirizzo nuovo = coordinate nuove. Si usano i campi normalizzati dal
      // geocoder, come fa il backend in registrazione.
      if (indirizzoCambiato.value && geo.punto) {
        const p = geo.punto;
        Object.assign(campi, {
          address: p.address || pulito(bozza.address),
          street_number: p.street_number || pulito(bozza.street_number),
          cap: p.cap || pulito(bozza.cap),
          city: p.city || pulito(bozza.city),
          province: pulito(p.province || bozza.province).toUpperCase(),
          latitude: p.latitude,
          longitude: p.longitude,
        });
      }

      salvando.value = true;
      try {
        await aggiornaCliente(id.value, campi);
        cliente.value = { ...cliente.value, ...campi };
        modifica.value = false;
        ElMessage({ type: 'success', message: 'Dati del cliente aggiornati.' });
      } catch (e) {
        console.error('Cliente non aggiornato:', e);
        ElMessage({ type: 'error', showClose: true, message: messageForError(e) });
      } finally {
        salvando.value = false;
      }
    };

    // ---- auto ---------------------------------------------------------
    const nomeAuto = (a) => [a.marca, a.modello].map(pulito).filter(Boolean).join(' ') || 'Auto';
    const cambio = (t) => {
      if (t === 'manual') return 'Manuale';
      if (t === 'automatic') return 'Automatico';
      if (t === 'both') return 'Automatico o manuale';
      return 'Cambio non indicato';
    };
    const normTarga = (t) => pulito(t).toUpperCase();

    const problemaAuto = (f, escludiId) => {
      if (!pulito(f.targa) || !pulito(f.marca) || !pulito(f.modello)) return 'Servono targa/telaio, marca e modello';
      if (auto.value.some((a) => a.id !== escludiId && normTarga(a.targa) === normTarga(f.targa))) {
        return "Questa targa/telaio è già su un'altra auto del cliente";
      }
      return '';
    };
    const campiAuto = (f) => ({
      targa: normTarga(f.targa),
      marca: pulito(f.marca),
      modello: pulito(f.modello),
      intestatario: pulito(f.intestatario) || nomeCliente.value,
      transmission: f.transmission,
    });

    // `esito` lo scrive PropostaValet: { problema, dati } per `proponiPacchetto`.
    const proposta = reactive({ carId: '', esito: null, tentato: false, invio: false });

    const modificaAuto = reactive({
      id: '', targa: '', marca: '', modello: '', intestatario: '', transmission: 'automatic', entrambi: false, salvando: false,
    });
    const cambiModifica = computed(() => (modificaAuto.entrambi ? [...CAMBI, CAMBIO_ENTRAMBI] : CAMBI));
    const apriModificaAuto = (a) => {
      Object.assign(modificaAuto, {
        id: a.id,
        targa: a.targa || '',
        marca: a.marca || '',
        modello: a.modello || '',
        intestatario: a.intestatario || '',
        // Un valore sconosciuto (o vuoto) parte da automatico; `both` si conserva.
        transmission: ['manual', 'automatic', 'both'].includes(a.transmission) ? a.transmission : 'automatic',
        entrambi: a.transmission === 'both',
      });
      proposta.carId = '';
    };
    const salvaModificaAuto = async () => {
      const problema = problemaAuto(modificaAuto, modificaAuto.id);
      if (problema) {
        ElMessage({ type: 'warning', message: problema });
        return;
      }
      modificaAuto.salvando = true;
      try {
        await aggiornaAuto(modificaAuto.id, campiAuto(modificaAuto));
        await caricaAuto();
        modificaAuto.id = '';
        ElMessage({ type: 'success', message: 'Auto aggiornata.' });
      } catch (e) {
        console.error('Auto non aggiornata:', e);
        ElMessage({ type: 'error', showClose: true, message: messageForError(e) });
      } finally {
        modificaAuto.salvando = false;
      }
    };

    const nuovaAuto = ref(null);
    const apriNuovaAuto = () => {
      nuovaAuto.value = {
        targa: '', marca: '', modello: '', intestatario: nomeCliente.value, transmission: 'automatic', salvando: false,
      };
    };
    const salvaNuovaAuto = async () => {
      const f = nuovaAuto.value;
      const problema = problemaAuto(f, null);
      if (problema) {
        ElMessage({ type: 'warning', message: problema });
        return;
      }
      f.salvando = true;
      try {
        await creaAuto({
          ...campiAuto(f),
          customer: { id: id.value },
          // Come in creazione cliente: chi legge le auto per sede deve trovarle.
          headquarters: contesto.sedi.map((h) => ({ id: h.id })),
        });
        await caricaAuto();
        nuovaAuto.value = null;
        ElMessage({ type: 'success', message: 'Auto aggiunta.' });
      } catch (e) {
        console.error('Auto non aggiunta:', e);
        ElMessage({ type: 'error', showClose: true, message: messageForError(e) });
        f.salvando = false;
      }
    };

    // ---- pacchetti valet ----------------------------------------------
    const partnerDi = (m) => (m.partner ? m.partner.id : m.partnerId);
    const pacchettiDi = (a) =>
      (a.minutes || [])
        .filter((m) => contesto.partnerId && partnerDi(m) === contesto.partnerId)
        .slice()
        .sort((x, y) => new Date(y.data_reg || 0) - new Date(x.data_reg || 0));

    const titoloPacchetto = (m) => {
      if (m.valet_per_year && m.duration_years) {
        const anni = m.duration_years === 1 ? '1 anno' : `${m.duration_years} anni`;
        return `${m.valet_per_year} valet all'anno per ${anni}`;
      }
      return `Pacchetto da ${num(m.valet)} valet`;
    };
    const percentuale = (m) => {
      const tot = num(m.valet);
      return tot > 0 ? Math.min(100, Math.round((num(m.residual_valet) / tot) * 100)) : 0;
    };

    const apriProposta = (a) => {
      Object.assign(proposta, { carId: a.id, esito: null, tentato: false, invio: false });
      modificaAuto.id = '';
    };
    const problemaProposta = computed(() => {
      if (!proposta.esito || proposta.esito.problema) {
        return proposta.esito?.problema || 'Prezzo del pacchetto non ancora calcolato';
      }
      if (!contesto.partnerId) return 'Dati del partner non disponibili: ricarica la pagina';
      return '';
    });

    // Crea il pacchetto, che resta da accettare. Al cliente NON parte più
    // nessuna email: la proposta arriva a Simba, ed è Simba ad accettarla.
    const inviaPropostaValet = async (a) => {
      proposta.tentato = true;
      if (problemaProposta.value) return;
      try {
        await ElMessageBox.confirm(
          `Creare il pacchetto per l'auto ${a.targa || nomeAuto(a)}? La proposta arriva a noi di Simba: saremo noi ad accettarla. Finché non è accettato, il pacchetto non si può usare nelle prenotazioni.`,
          'Proponi pacchetto valet',
          { confirmButtonText: 'Proponi a Simba', cancelButtonText: 'Annulla', type: 'info' },
        );
      } catch (e) {
        return; // annullato
      }

      proposta.invio = true;
      try {
        await proponiPacchetto({
          carId: a.id,
          partnerId: contesto.partnerId,
          ...proposta.esito.dati,
        });
        ElMessage({ type: 'success', message: 'Pacchetto proposto: lo accetteremo noi di Simba.' });
      } catch (e) {
        console.error('Pacchetto non creato:', e);
        ElMessage({ type: 'error', showClose: true, message: messageForError(e) });
        proposta.invio = false;
        return;
      }
      proposta.carId = '';
      proposta.invio = false;
      try {
        await caricaAuto();
      } catch (e) {
        console.error('Auto non ricaricate:', e);
      }
    };

    // ---- prenotazioni -------------------------------------------------
    const servizioDi = (b) => {
      if (b.type_booking === 'restaurant') return 'restaurant';
      if (b.type_prenotazione === 'escort') return b.loaner ? 'escort_loaner' : 'escort';
      return b.loaner ? 'pickup_loaner' : 'pickup';
    };
    const sede = (b) => (b.headquarter ? pulito(b.headquarter.name) || pulito(b.headquarter.city) || '—' : '—');
    const apriPrenotazione = (b) => router.push(`/agenda/prenotazione/${b.id}`);

    // ---- riepilogo ----------------------------------------------------
    const pacchettiDelPartner = computed(() => auto.value.flatMap(pacchettiDi));
    const valetUtilizzabili = computed(() =>
      pacchettiDelPartner.value
        .filter((m) => statoPacchetto(m).utilizzabile)
        .reduce((t, m) => t + num(m.residual_valet), 0),
    );
    const inAttesa = computed(() => pacchettiDelPartner.value.filter((m) => m.accepted !== true).length);
    const ultimaPrenotazione = computed(() => {
      const b = prenotazioni.righe.find((x) => x.state !== 'canceled');
      return b ? data(b.data) : '—';
    });

    return {
      CAMBI,
      LIMITE_PRENOTAZIONI,
      stato,
      cliente,
      auto,
      prenotazioni,
      carica,
      indietro,
      isAzienda,
      nomeCliente,
      iniziali,
      contatti,
      puoPrenotare,
      rottaPrenotazione,
      campiLettura,
      data,
      dataOra,
      euro,
      num,
      modifica,
      tentato,
      salvando,
      bozza,
      geo,
      iniziaModifica,
      annullaModifica,
      indirizzoCambiato,
      verificaIndirizzo,
      onProvinciaBlur,
      primoProblema,
      salvaCliente,
      nomeAuto,
      cambio,
      modificaAuto,
      cambiModifica,
      apriModificaAuto,
      salvaModificaAuto,
      nuovaAuto,
      apriNuovaAuto,
      salvaNuovaAuto,
      pacchettiDi,
      usiDi,
      titoloPacchetto,
      percentuale,
      statoPacchetto,
      // Le sedi del partner servono al prezzo della proposta: senza esporre
      // `contesto` il template leggeva `undefined.sedi` e «Proponi un
      // pacchetto valet» non apriva nulla.
      contesto,
      proposta,
      apriProposta,
      problemaProposta,
      inviaPropostaValet,
      prezzoCliente,
      servizioDi,
      sede,
      apriPrenotazione,
      bookingState,
      valetUtilizzabili,
      inAttesa,
      ultimaPrenotazione,
    };
  },
};
</script>

<style scoped>
.csd {
  max-width: 1180px;
  padding: 4px 8px 32px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ---- intestazione ---- */
.csd__head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.csd__back {
  width: 34px; height: 34px; flex: none; display: grid; place-items: center;
  border: 1px solid var(--color-border); border-radius: 6px; background: var(--color-bg-primary);
  color: var(--color-text-secondary); font-size: 20px; line-height: 1; cursor: pointer;
}
.csd__back:hover { border-color: var(--color-primary); color: var(--color-primary); }
.csd__avatar {
  width: 42px; height: 42px; flex: none; display: grid; place-items: center; border-radius: 50%;
  background: var(--color-bg-tertiary); color: #232c51; font-size: 14px; font-weight: 700; letter-spacing: 0.02em;
}
.csd__who { min-width: 0; }
.csd__title {
  margin: 0; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  font-size: 23px; font-weight: 700; letter-spacing: -0.015em; color: var(--color-text-primary);
}
.csd__sub { margin: 3px 0 0; font-size: 13px; color: var(--color-text-secondary); }
.csd__spacer { flex: 1 1 auto; }
.csd__cta {
  display: inline-flex; align-items: center; font-size: 13px; font-weight: 600; text-decoration: none;
  color: #fff; background: var(--color-primary); border-radius: 6px; padding: 8px 14px;
}
.csd__cta:hover { background: var(--color-primary-light); }

/* ---- impaginazione ---- */
.csd__grid { display: grid; grid-template-columns: minmax(0, 1fr) 280px; gap: 16px; align-items: start; }
.csd__col { display: flex; flex-direction: column; gap: 16px; padding: 0; }
.csd__aside { position: sticky; top: 12px; }
.csd__body { padding: 16px; display: flex; flex-direction: column; gap: 14px; }
.csd__body--kv { gap: 0; padding-top: 6px; padding-bottom: 6px; }
.csd__count {
  font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; color: var(--color-text-secondary);
  background: var(--color-bg-tertiary); border-radius: 999px; padding: 1px 8px;
}

/* ---- dati in lettura ---- */
.csd__kvGrid { margin: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px 18px; }
.csd__kvItem { min-width: 0; }
.csd__kvItem--full { grid-column: 1 / -1; }
.csd__kvItem dt {
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-muted);
}
.csd__kvItem dd { margin: 3px 0 0; font-size: 13.5px; color: var(--color-text-primary); overflow-wrap: anywhere; }
.csd__kvItem dd.is-empty { color: var(--color-text-muted); }

/* ---- campi ---- */
.csd__grid3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px 16px; }
.csd__f { display: flex; flex-direction: column; min-width: 0; }
.csd__f--2 { grid-column: span 2; }
.csd__f--full { grid-column: 1 / -1; }
.csd__f :deep(.el-input-number),
.csd__f :deep(.el-date-editor.el-input) { width: 100%; }
.csd__req { color: var(--state-danger); }
.csd__h4 {
  margin: 4px 0 0; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--color-text-secondary);
}
.csd__help { margin-top: 5px; font-size: 11.5px; color: var(--color-text-secondary); }
.csd__plateInput :deep(.el-input__inner) {
  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 650; font-variant-numeric: tabular-nums;
}
.csd__formActions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.csd__formActions .el-button + .el-button { margin-left: 0; }
.csd__err { font-size: 12.5px; font-weight: 600; color: var(--state-attn); }

.csd__seg { display: inline-flex; align-self: flex-start; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; }
.csd__seg button {
  font: inherit; font-size: 13px; font-weight: 600; height: 30px; padding: 0 14px; border: 0;
  background: var(--color-bg-primary); color: var(--color-text-secondary); cursor: pointer;
}
.csd__seg button + button { border-left: 1px solid #dcdfe6; }
.csd__seg button.is-on { background: var(--color-primary); color: #fff; }

.csd__back:focus-visible,
.csd__seg button:focus-visible,
.csd__bk:focus-visible,
.csd__cta:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }

/* ---- avvisi ---- */
.csd__banner {
  display: flex; align-items: flex-start; gap: 10px; padding: 11px 13px;
  border: 1px solid; border-radius: 6px; font-size: 12.5px; line-height: 1.5;
}
.csd__bannerIcon {
  flex: none; width: 18px; height: 18px; margin-top: 1px; display: grid; place-items: center;
  border-radius: 50%; font-size: 11px; font-weight: 800; color: #fff;
}
.csd__bannerBody { flex: 1 1 auto; min-width: 0; }
.csd__bannerBody strong { font-weight: 650; }
.csd__bannerActions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 9px; }
.csd__banner--ok { color: var(--state-ok); background: var(--state-ok-bg); border-color: var(--state-ok-line); }
.csd__banner--ok .csd__bannerIcon { background: var(--state-ok); }
.csd__banner--attn { color: #7c2d0a; background: var(--state-attn-bg); border-color: var(--state-attn-line); }
.csd__banner--attn .csd__bannerIcon { background: var(--state-attn); }
.csd__banner--danger { color: #6e1a15; background: var(--state-danger-bg); border-color: var(--state-danger-line); }
.csd__banner--danger .csd__bannerIcon { background: var(--state-danger); }

/* ---- auto ---- */
.csd__empty { margin: 0; font-size: 12.5px; color: var(--color-text-secondary); }
.csd__empty--pad { padding: 18px 16px; }
.csd__car {
  display: flex; flex-direction: column; gap: 12px; padding: 14px;
  border: 1px solid var(--color-border); border-radius: 6px; background: var(--color-bg-secondary);
}
.csd__car--new { border-style: dashed; }
.csd__carHead { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.csd__plate {
  font-size: 14px; font-weight: 700; letter-spacing: 0.06em; font-variant-numeric: tabular-nums;
  background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 4px; padding: 2px 8px;
}
.csd__carName { font-size: 13.5px; font-weight: 600; color: var(--color-text-primary); }
.csd__carTag {
  font-size: 11px; font-weight: 650; color: var(--state-muted); background: var(--state-muted-bg);
  border-radius: 20px; padding: 1px 8px;
}
.csd__carLink { font-size: 12.5px; font-weight: 600; color: var(--color-primary); text-decoration: none; }
.csd__carLink:hover { text-decoration: underline; }
.csd__meta { margin-top: -6px; font-size: 12px; color: var(--color-text-muted); }
.csd__addCar,
.csd__propose { align-self: flex-start; }

/* ---- pacchetti ---- */
.csd__valets { display: flex; flex-direction: column; gap: 8px; }
.csd__valet {
  display: flex; flex-direction: column; gap: 7px; padding: 10px 12px;
  background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 6px;
}
.csd__valetTop { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.csd__valetTitle { font-size: 13px; font-weight: 650; color: var(--color-text-primary); }
.csd__bar { height: 6px; border-radius: 3px; background: var(--color-bg-tertiary); overflow: hidden; }
.csd__bar span { display: block; height: 100%; background: var(--state-ok); border-radius: 3px; }
.csd__bar span.is-low { background: var(--state-attn); }
.csd__valetMeta {
  display: flex; flex-wrap: wrap; gap: 4px 16px; font-size: 12px; color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;
}
.csd__valetMeta b { color: var(--color-text-primary); font-weight: 700; }
.csd__noValet { margin: 0; font-size: 12px; color: var(--color-text-muted); }
.csd__proposal {
  display: flex; flex-direction: column; gap: 12px; padding-top: 12px; border-top: 1px dashed #dcdfe6;
}
.csd__proposalTitle {
  font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--color-text-secondary);
}
.csd__proposal .fx-stats { margin-top: 0; }
.csd__note { margin: 0; max-width: 78ch; font-size: 12px; line-height: 1.5; color: var(--color-text-secondary); }

/* ---- prenotazioni ---- */
.csd__list { display: flex; flex-direction: column; }
.csd__bk {
  display: grid; grid-template-columns: 132px 180px 100px minmax(0, 1fr) auto 14px; align-items: center; gap: 14px;
  width: 100%; padding: 11px 16px; font: inherit; text-align: left; cursor: pointer;
  background: none; border: 0; border-top: 1px solid var(--color-bg-tertiary);
}
.csd__bk:first-child { border-top: 0; }
.csd__bk:hover { background: var(--color-bg-secondary); }
.csd__bkDate { font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; }
.csd__bkCar { font-size: 12.5px; font-weight: 650; letter-spacing: 0.04em; font-variant-numeric: tabular-nums; }
.csd__bkSede {
  font-size: 12.5px; color: var(--color-text-secondary); overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
}
.csd__chev { font-size: 16px; color: var(--color-text-muted); }
.csd__hint { margin: 0; padding: 10px 16px; font-size: 12px; color: var(--color-text-muted); }

/* ---- riepilogo ---- */
.csd__kv {
  display: flex; justify-content: space-between; gap: 12px; padding: 9px 0; font-size: 13px;
  border-bottom: 1px solid var(--color-bg-tertiary);
}
.csd__kv:last-child { border-bottom: 0; }
.csd__kv span { color: var(--color-text-secondary); }
.csd__kv b { font-weight: 650; color: var(--color-text-primary); font-variant-numeric: tabular-nums; text-align: right; }
.csd__kv b.csd__accent { color: var(--state-ok); }
.csd__kv b.csd__attn { color: var(--state-attn); }

@media (max-width: 1000px) {
  .csd__grid { grid-template-columns: 1fr; }
  .csd__aside { position: static; }
  .csd__bk { grid-template-columns: 120px minmax(0, 1fr) auto 14px; }
  .csd__bkCar,
  .csd__bkSede { display: none; }
}
@media (max-width: 700px) {
  .csd__grid3,
  .csd__kvGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 520px) {
  .csd__grid3,
  .csd__kvGrid { grid-template-columns: 1fr; }
  .csd__f--2 { grid-column: 1 / -1; }
}
</style>
